import { useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';
import type { HtmlMarkerSetExtendedProps } from '../types';
import { HtmlMarker } from './HtmlMarker';
import { useInterval } from '../helpers/hooks';
import { VisibilityBoxMode } from './VisibilityBox';

const DEFAULT_DISTANCE_THRESHOLD = 100;

export const HtmlMarkerSet = ({
  instances: markerInstances,
  enableFilteringByVisibilityBox = true,
  visibilityBox,
  maxMarkersToShow = 100,
  distanceThreshold,
}: HtmlMarkerSetExtendedProps) => {
  const { camera } = useThree();
  const [cameraPosition, setCameraPosition] = useState(camera.position.clone());

  useInterval(() => {
    if (cameraPosition.distanceToSquared(camera.position) > 1) {
      setCameraPosition(camera.position.clone());
    }
  }, 1000);

  const vboxFilter =
    enableFilteringByVisibilityBox && visibilityBox.mode !== VisibilityBoxMode.Disabled
      ? visibilityBox
      : undefined;

  const vboxFilteredInstances = useMemo(
    () =>
      vboxFilter
        ? markerInstances.filter(({ position }) => vboxFilter.checkIsInside(position))
        : markerInstances,
    [markerInstances, vboxFilter]
  );

  // Calculate visible instances based on camera position
  const visibleInstances = useMemo(() => {
    const instancesWithDistance = vboxFilteredInstances
      .map((instance) => {
        const { x, y, z } = cameraPosition;
        const [x1, y1, z1] = instance.position;
        const dx = x1 - x;
        const dy = y1 - y;
        const dz = z1 - z;
        return { instance, distanceSquared: dx * dx + dy * dy + dz * dz };
      })
      .filter(({ instance, distanceSquared }) => {
        const threshold =
          instance.distanceThreshold ?? distanceThreshold ?? DEFAULT_DISTANCE_THRESHOLD;
        return distanceSquared < threshold * threshold;
      });

    // show only closest markers upto maxMarkersToShow
    instancesWithDistance.sort((a, b) => a.distanceSquared - b.distanceSquared);
    instancesWithDistance.splice(maxMarkersToShow);

    return instancesWithDistance.map(({ instance }) => instance);
  }, [vboxFilteredInstances, cameraPosition, maxMarkersToShow, distanceThreshold]);

  const markerComponents = useMemo(() => {
    return visibleInstances.map(
      ({
        id,
        position,
        divStyle,
        onClick,
        onHover,
        children,
        scale,
        markerHeight,
        width,
        height,
        fixedOnScreenSize,
      }) => (
        <HtmlMarker
          key={id}
          position={position}
          divStyle={divStyle}
          onClick={onClick}
          onHover={onHover}
          scale={scale}
          markerHeight={markerHeight}
          fixedOnScreenSize={fixedOnScreenSize}
          width={width}
          height={height}
        >
          {children}
        </HtmlMarker>
      )
    );
  }, [visibleInstances]);

  return <>{markerComponents}</>;
};
